import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import { BRAND, IMAGES, SERVICES, SECTORS, STATS, WHY, TESTIMONIALS } from "../mock";

function Reveal({ children, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && el.classList.add("in-view"),
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-up ${className}`}>
      {children}
    </div>
  );
}

function ServiceIcon({ name, className }) {
  const Cmp = Icons[name] || Icons.Building2;
  return <Cmp className={className} />;
}

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden bg-[#1F3463] lg:min-h-[92vh]">
        <div className="absolute inset-0">
          <img
            src={IMAGES.heroHomes}
            alt="New build homes"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F3463] via-[#1F3463]/85 to-[#1F3463]/40" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-12 pt-44 sm:pt-40 lg:px-8 lg:pb-0 lg:pt-28">
          <div className="max-w-2xl">
            <span className="mb-5 inline-block rounded-full border border-[#C9A876]/50 bg-[#C9A876]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#C9A876]">
              Property · Development · Construction
            </span>
            <h1 className="font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
              Building Homes,{" "}
              <span className="text-[#C9A876]">Shaping Communities,</span> Creating
              Legacies
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/80">
              Boulter Group is a family-run property company with a clear
              purpose: to help young people own their first home. We invest in,
              build, manage and maintain property to make that possible.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/services"
                className="btn-gold flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold uppercase tracking-wide"
              >
                Our Services <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/about"
                className="btn-outline-light rounded-md px-7 py-3.5 text-sm font-semibold uppercase tracking-wide"
              >
                Who We Are
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-[#15264a]">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-5 lg:grid-cols-4 lg:px-8">
          {STATS.map((s) => (
            <div key={s.label} className="px-4 py-8 text-center">
              <div className="font-display text-3xl font-bold text-[#C9A876] lg:text-4xl">
                {s.value}
              </div>
              <div className="mt-1 text-xs uppercase tracking-widest text-white/60">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
                What We Do
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-[#1F3463] sm:text-4xl">
                Working across the whole property journey
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-slate-600">
                We want to make home ownership achievable for young people,
                whatever their background and without relying on inherited wealth.
                From land and investment through to construction, lettings and
                maintenance, we cover every stage of the property journey.
              </p>
              <ul className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SERVICES.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 text-slate-700">
                    <Check className="h-5 w-5 text-[#C9A876]" />
                    <span className="font-medium">{s.name}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/services"
                className="btn-navy mt-9 inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold uppercase tracking-wide"
              >
                Explore Services <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
            <Reveal className="relative">
              <div className="img-zoom overflow-hidden rounded-2xl shadow-2xl">
                <img src={IMAGES.happyCouple} alt="Happy new homeowners" className="h-[460px] w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden rounded-xl bg-[#1F3463] px-8 py-6 shadow-xl sm:block">
                <div className="font-display text-3xl font-bold text-[#C9A876]">£35k–£8M</div>
                <div className="text-xs uppercase tracking-widest text-white/70">Project range</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTORS SPLIT */}
      <section className="bg-[#F7F8FA] py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
              Two Sides, One Vision
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-[#1F3463] sm:text-4xl">
              Clearly defined sectors
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Whether you're a homeowner, landlord, landowner or investor — we
              have a dedicated team for you.
            </p>
          </Reveal>
          <div className="grid gap-8 lg:grid-cols-2">
            {SECTORS.map((sec) => (
              <Reveal key={sec.id}>
                <div className="card-lift group relative h-[420px] overflow-hidden rounded-2xl">
                  <img
                    src={sec.image}
                    alt={sec.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#15264a] via-[#15264a]/60 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-8">
                    <h3 className="font-display text-3xl font-bold text-white">{sec.title}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-white/80">{sec.blurb}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {sec.points.map((p) => (
                        <span key={p} className="rounded-full border border-[#C9A876]/40 bg-[#C9A876]/15 px-3 py-1 text-xs font-medium text-[#C9A876]">
                          {p}
                        </span>
                      ))}
                    </div>
                    <Link to={sec.to} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:text-[#C9A876]">
                      Discover more <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
              Our Expertise
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-[#1F3463] sm:text-4xl">
              Six specialisms, one trusted partner
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Reveal key={s.id}>
                <Link
                  to={`/services#${s.id}`}
                  className="card-lift group block h-full overflow-hidden rounded-2xl border border-slate-100 bg-white"
                >
                  <div className="img-zoom h-44">
                    <img src={s.image} alt={s.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="p-7">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1F3463] transition-colors group-hover:bg-[#1F3463] group-hover:text-[#C9A876]">
                      <ServiceIcon name={s.icon} className="h-6 w-6" />
                    </div>
                    <h3 className="font-display text-xl font-semibold text-[#1F3463]">{s.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C9A876]">
                      Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="brand-texture bg-[#1F3463] py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
              Why Choose Us
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">
              Reasons to build with the Boulter Group
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w) => (
              <Reveal key={w.title}>
                <div className="card-lift h-full rounded-2xl border border-white/10 bg-white/5 p-7">
                  <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#C9A876] text-[#15264a]">
                    <ServiceIcon name={w.icon} className="h-7 w-7" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <img src={IMAGES.luxuryDusk} alt="Luxury home at dusk" className="h-full w-full object-cover absolute inset-0" />
        <div className="absolute inset-0 bg-[#15264a]/85" />
        <div className="relative mx-auto max-w-3xl px-5 py-24 text-center lg:px-8">
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
            Ready to start your project?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-white/80">
            If you wish to get in touch, we'd be glad to have a friendly chat
            about how we can help with your property plans.
          </p>
          <Link
            to="/contact"
            className="btn-gold mt-9 inline-flex items-center gap-2 rounded-md px-8 py-4 text-sm font-semibold uppercase tracking-wide"
          >
            Get in touch <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
